import { useEffect, useState } from 'react';

/**
 * Visibility control for the WebRTC diagnostics overlay (see `WebrtcMetrics`).
 *
 * The overlay is a debug/support tool, not an end-user feature, so it is OFF by
 * default and never shown unless someone explicitly opts in. Two opt-in channels:
 *
 *   1. Console:  window.enableWebrtcOverlay()  /  window.disableWebrtcOverlay()
 *   2. URL param: ?webrtcOverlay=1  (any of "", "1", "true"; anything else = off)
 *
 * The choice is mirrored into localStorage so it survives reloads — important when
 * reproducing a streaming issue — but nothing writes `true` by default, so a fresh
 * browser never sees the overlay. `disableWebrtcOverlay()` (or `?webrtcOverlay=0`)
 * clears it.
 */

const STORAGE_KEY = 'webrtcOverlayEnabled';
const URL_PARAM = 'webrtcOverlay';
// Same-tab console toggles don't fire the `storage` event, so we bridge them with
// a custom event that the hook below listens to for a live show/hide.
const CHANGE_EVENT = 'webrtc-overlay-change';

declare global {
  interface Window {
    enableWebrtcOverlay?: () => string;
    disableWebrtcOverlay?: () => string;
  }
}

function persist(enabled: boolean): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(enabled));
  } catch {
    // localStorage can throw in private mode / when disabled — the overlay just
    // won't persist across reloads, which is acceptable for a debug tool.
  }
}

function setEnabled(enabled: boolean): string {
  persist(enabled);
  window.dispatchEvent(new CustomEvent<boolean>(CHANGE_EVENT, { detail: enabled }));
  return `WebRTC overlay ${enabled ? 'enabled' : 'disabled'}.`;
}

function readInitial(): boolean {
  try {
    const raw = new URLSearchParams(window.location.search).get(URL_PARAM);
    if (raw !== null) {
      // URL param wins and is persisted, so it also survives client-side navigation
      // that drops the query string.
      const enabled = raw === '' || raw === '1' || raw === 'true';
      persist(enabled);
      return enabled;
    }
    return window.localStorage.getItem(STORAGE_KEY) === JSON.stringify(true);
  } catch {
    return false;
  }
}

// Registered once at import time so the helpers are callable from the console the
// moment the app bundle loads, even before any game page has mounted.
if (typeof window !== 'undefined') {
  window.enableWebrtcOverlay = () => setEnabled(true);
  window.disableWebrtcOverlay = () => setEnabled(false);
}

export function useWebrtcOverlay(): boolean {
  const [enabled, setState] = useState<boolean>(readInitial);

  useEffect(() => {
    const onChange = (e: Event) => setState((e as CustomEvent<boolean>).detail);
    const onStorage = (e: StorageEvent) => {
      // Cross-tab sync: reflect toggles made in another tab.
      if (e.key === STORAGE_KEY) setState(e.newValue === JSON.stringify(true));
    };
    window.addEventListener(CHANGE_EVENT, onChange);
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener(CHANGE_EVENT, onChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  return enabled;
}

export default useWebrtcOverlay;
